import { useEffect, useRef, useState } from "react";
import { Loader2, CameraOff } from "lucide-react";

const HTML5_QRCODE_SRC = "https://unpkg.com/html5-qrcode@2.3.8/html5-qrcode.min.js";

const loadScript = (src) =>
  new Promise((resolve, reject) => {
    if (window.Html5Qrcode) return resolve();
    const existing = document.querySelector(`script[src="${src}"]`);
    if (existing) {
      existing.addEventListener("load", () => resolve());
      existing.addEventListener("error", reject);
      return;
    }
    const s = document.createElement("script");
    s.src = src;
    s.async = true;
    s.onload = () => resolve();
    s.onerror = reject;
    document.body.appendChild(s);
  });

/**
 * BarcodeScanner – camera-based Code 128 reader.
 * Props: onDetected(text), active(bool)
 */
const BarcodeScanner = ({ onDetected, active = true }) => {
  const elementId = "html5-qrcode-scanner-region";
  const scannerRef = useRef(null);
  const startedRef = useRef(false);
  const startingRef = useRef(null); // in-flight scanner.start() — stop must wait for it
  const [status, setStatus] = useState("loading"); // loading | running | error
  const [error, setError] = useState("");

  useEffect(() => {
    let cancelled = false;

    const start = async () => {
      try {
        await loadScript(HTML5_QRCODE_SRC);
        if (cancelled || !window.Html5Qrcode) return;

        const { Html5Qrcode, Html5QrcodeSupportedFormats } = window;
        const scanner = new Html5Qrcode(elementId, { verbose: false });
        scannerRef.current = scanner;

        const config = {
          fps: 12,
          qrbox: (vw, vh) => {
            const w = Math.min(vw * 0.8, 360);
            const h = Math.min(vh * 0.4, 160);
            return { width: w, height: h };
          },
          aspectRatio: 1.6,
          formatsToSupport: [Html5QrcodeSupportedFormats.QR_CODE],
        };

        startingRef.current = scanner.start(
          { facingMode: "environment" },
          config,
          (decodedText) => {
            if (decodedText && onDetected) onDetected(decodedText);
          },
          () => {} // silent on scan failure tick
        );
        await startingRef.current;

        if (cancelled) {
          try { await scanner.stop(); } catch (e) { if (process.env.NODE_ENV !== "production") console.debug("scanner.stop on cancel:", e); }
          return;
        }
        startedRef.current = true;
        setStatus("running");
      } catch (err) {
        if (cancelled) return; // start interrupted by stop/unmount — not a real error
        console.error("Scanner init error", err);
        setError(err?.message || "Camera unavailable");
        setStatus("error");
      }
    };

    if (active) start();

    return () => {
      cancelled = true;
      const s = scannerRef.current;
      const pending = startingRef.current;
      if (s && (startedRef.current || pending)) {
        // Wait for an in-flight start so the <video> isn't removed mid play() (html5-qrcode AbortError).
        Promise.resolve(pending).catch(() => {})
          .then(() => s.stop().catch(() => {}))
          .finally(() => {
            try { s.clear(); } catch (e) { if (process.env.NODE_ENV !== "production") console.debug("scanner.clear on unmount:", e); }
          });
        startedRef.current = false;
        startingRef.current = null;
      }
    };
  }, [active, onDetected]);

  return (
    <div
      data-testid="barcode-scanner"
      className="scanner-container w-full aspect-[4/3] sm:aspect-[16/9] relative"
    >
      <div id={elementId} className="absolute inset-0 w-full h-full" />

      {status === "running" && (
        <>
          <div className="scanner-reticle">
            <span className="corner tl" />
            <span className="corner tr" />
            <span className="corner bl" />
            <span className="corner br" />
          </div>
          <div className="absolute bottom-3 left-1/2 -translate-x-1/2 px-3 py-1.5 bg-black/70 text-white text-xs tracking-wider uppercase font-semibold rounded-sm">
            Aim barcode inside frame
          </div>
        </>
      )}

      {status === "loading" && (
        <div className="absolute inset-0 flex flex-col items-center justify-center text-white gap-3 bg-slate-900">
          <Loader2 className="h-8 w-8 animate-spin" />
          <p className="text-sm tracking-wide">Starting camera…</p>
        </div>
      )}

      {status === "error" && (
        <div
          data-testid="scanner-error"
          className="absolute inset-0 flex flex-col items-center justify-center bg-slate-900 text-white p-6 text-center gap-3"
        >
          <CameraOff className="h-8 w-8" />
          <p className="text-sm font-semibold">Camera unavailable</p>
          <p className="text-xs text-slate-300 max-w-xs">
            {error || "Allow camera access and use HTTPS. You can also enter the barcode manually below."}
          </p>
        </div>
      )}
    </div>
  );
};

export default BarcodeScanner;
